import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useDownloadApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Vendor, VendorForm, VendorSearchForm } from 'entities/vendor'

type VendorsResponse = PagingResponse & {
    results: Vendor[]
}

export function useFetchVendorsApi(searchForm?: Form<VendorSearchForm>): IndexApiSet<VendorsResponse> & { execute: () => void } {
    const apiPath = 'vendors/'
    const api = useIndexApi<VendorsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1, perPage: searchForm?.object?.perPage || 50 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function useDownloadCsvVendorsApi(searchForm: Form<VendorSearchForm>): ApiSet<BaseResponse> & { execute: () => void } {
    const apiPath = 'vendors/csv/'
    const api = useDownloadApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (): void => {
        api.execute(apiPath, 'vendor', searchForm.object)
    }

    return { ...api, execute: execute }
}

export function usePostVendorApi(): ApiSet<BaseResponse> & { execute: (form: Form<VendorForm>) => void } {
    const apiSet = usePostApi<BaseResponse, VendorForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<VendorForm>) => {
        const apiPath = `vendors/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutVendorApi(): ApiSet<BaseResponse> & { execute: (form: Form<VendorForm>) => void } {
    const apiSet = usePutApi<BaseResponse, VendorForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<VendorForm>) => {
        const apiPath = `vendors/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutVendorsApi(): ApiSet<BaseResponse> & { execute: (form: Form<VendorForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, VendorForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<VendorForm[]>) => {
        const apiPath = `vendors/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteVendorApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `vendors/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
